const base = "https://d1dlav2y8bte65.cloudfront.net";
const prefix = "Twitch_Compact";
const requests = new Map();

function trimRequests() {
  for (const [key, entry] of requests) {
    if (requests.size <= 160) break;
    if (!entry.pending) requests.delete(key);
  }
}

export function compactJson(key, ttl = Infinity) {
  if (!key.startsWith(`${prefix}/`) || key.includes("..") || key.includes("?")) {
    return Promise.reject(new Error("Invalid compact Twitch object key."));
  }
  const previous = requests.get(key);
  if (previous && previous.until > Date.now()) return previous.promise;
  const entry = { until: Infinity, pending: true };
  entry.promise = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(`${base}/${key}`, { cache: "no-cache", signal: controller.signal });
      if (!response.ok) throw new Error(`Twitch export request failed (HTTP ${response.status}).`);
      const result = await response.json();
      entry.until = Date.now() + ttl;
      entry.pending = false;
      trimRequests();
      return result;
    } finally {
      clearTimeout(timer);
    }
  })().catch(error => {
    if (requests.get(key) === entry) requests.delete(key);
    throw error;
  });
  requests.delete(key);
  requests.set(key, entry);
  trimRequests();
  return entry.promise;
}

export async function loadTwitchManifest(dataset) {
  if (!/^(cards\/(now|1d|7d)|streamers\/(now|1d|7d)|history\/(daily|monthly)|leaderboards\/(1d|7d)\/(summary|games))$/.test(dataset)) {
    throw new Error("Unsupported compact Twitch dataset.");
  }
  const manifest = await compactJson(`${prefix}/${dataset}/manifest.json`, 10000);
  if (manifest.schemaVersion !== 1 || manifest.dataset !== dataset || !manifest.revision) {
    throw new Error("Invalid compact Twitch manifest.");
  }
  return manifest;
}

function extractTableRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") throw new Error("Invalid Twitch streamer table.");
  const rows = payload.streamers || payload.rows || payload.streamer_games;
  if (!Array.isArray(rows)) throw new Error("Invalid Twitch streamer table.");
  return rows;
}

export async function loadTwitchTablePages(
  dataset,
  onRows = () => {},
  { maxPages = Infinity, shouldContinue = () => true } = {}
) {
  const manifest = await loadTwitchManifest(dataset);
  const table = manifest.table;
  if (!table) throw new Error("Missing compact Twitch streamer table.");

  if (table.key) {
    const rows = extractTableRows(await compactJson(table.key));
    onRows(rows, 0, 1);
    return { rowCount: rows.length, pageCount: 1, loadedPages: 1, complete: true };
  }

  if (!Array.isArray(table.pages) || !Number.isInteger(table.rowCount) || table.rowCount < 1 ||
      !Number.isInteger(table.pageSize) || table.pageSize < 1) {
    throw new Error("Invalid compact Twitch table index.");
  }
  if (table.pages.some(page =>
    !page || !Number.isInteger(page.rowCount) || page.rowCount < 1 || !page.key
  )) {
    throw new Error("Invalid compact Twitch table pages.");
  }
  const expectedRows = table.pages.reduce((total, page) => total + Number(page.rowCount || 0), 0);
  if (expectedRows !== table.rowCount ||
      (Number.isInteger(manifest.rowCount) && manifest.rowCount !== table.rowCount)) {
    throw new Error("Invalid compact Twitch table pages.");
  }

  const limit = Math.min(table.pages.length, Number.isInteger(maxPages) && maxPages > 0 ? maxPages : table.pages.length);
  const loadPage = async index => {
    const page = table.pages[index];
    const rows = extractTableRows(await compactJson(page.key));
    if (rows.length !== page.rowCount) throw new Error("Twitch streamer page row count mismatch.");
    return rows;
  };

  const first = await loadPage(0);
  if (shouldContinue()) onRows(first, 0, table.pages.length);
  let loadedPages = 1;
  for (let start = 1; start < limit; start += 4) {
    if (!shouldContinue()) break;
    const end = Math.min(limit, start + 4);
    const pages = await Promise.all(Array.from({ length: end - start }, (_, offset) => loadPage(start + offset)));
    pages.forEach((rows, offset) => {
      if (shouldContinue()) onRows(rows, start + offset, table.pages.length);
      loadedPages += 1;
    });
  }
  return {
    rowCount: table.rowCount,
    pageCount: table.pages.length,
    loadedPages,
    complete: loadedPages === table.pages.length,
  };
}

export async function loadTwitchCards(period) {
  const manifest = await loadTwitchManifest(`cards/${period}`);
  const snapshot = await compactJson(manifest.snapshot.key);
  if (snapshot.schemaVersion !== 1 || snapshot.period !== period ||
      !Array.isArray(snapshot.fields) || !Array.isArray(snapshot.rows) ||
      snapshot.rows.length !== manifest.gameCount) {
    throw new Error("Invalid Twitch card snapshot.");
  }
  const games = snapshot.rows.map(values => {
    if (!Array.isArray(values) || values.length !== snapshot.fields.length) throw new Error("Invalid Twitch card row.");
    const row = Object.fromEntries(snapshot.fields.map((field, i) => [field, values[i]]));
    if (!/^[A-Za-z0-9_-]{1,80}$/.test(row.id) || typeof row.name !== "string" || !Number.isFinite(row.viewMinutes)) {
      throw new Error("Invalid Twitch card metrics.");
    }
    return { ...row, key: row.id };
  });
  return { games, generatedAt: manifest.generatedAt, revision: manifest.revision };
}

export async function loadTwitchGame(dataset, gameId) {
  const id = String(gameId ?? "");
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(id)) throw new Error("A Twitch game ID is required.");
  const manifest = await loadTwitchManifest(dataset);
  if (!Number.isInteger(manifest.shardCount) || manifest.shardCount < 1 || manifest.shardCount > 256) {
    throw new Error("Invalid Twitch routing index.");
  }
  const number = /^\d+$/.test(id) ? Number(id) : [...id].reduce((sum, character) => sum + character.charCodeAt(0), 0);
  const route = manifest.indexes?.[String(number % manifest.shardCount)];
  if (!route) throw new Error("Missing Twitch routing index.");
  const index = await compactJson(route.key);
  const hash = index[id];
  if (hash == null) return null;
  if (!/^[a-f0-9]{32}$/.test(hash)) throw new Error("Invalid Twitch game revision.");
  const record = await compactJson(`${prefix}/${dataset}/games/${id}/${hash}.json.gz`);
  if (String(record?.game_id) !== id) throw new Error("Twitch game ID does not match its export.");
  return record;
}

export async function loadTwitchLiveStreamers(onRows, options) {
  return loadTwitchTablePages("streamers/now", onRows, options);
}

export async function loadTwitchNameIndex() {
  const manifest = await loadTwitchManifest("history/daily");
  if (!manifest.catalog?.key) throw new Error("Missing compact Twitch title catalog.");
  const rows = await compactJson(manifest.catalog.key);
  if (!Array.isArray(rows)) throw new Error("Invalid Twitch title catalog.");
  return rows;
}
